import {
  useCallback,
  useEffect,
  useState,
  useRef,
  useLayoutEffect,
} from 'preact/hooks';
import {RefObject} from 'preact';

import {debounce} from 'utilities/common';
import {getStyleTags} from 'utilities/shadow-style';

import {getStorage} from './helpers';
import {CacheKey, CacheValueMapping, TypedStorage} from './types';

/**
 *
 * @param event
 * @param handler
 * @param deps
 * @param eventTarget
 */
export function useListener<
  TEvent extends Event,
  TKey extends keyof (DocumentEventMap & WindowEventHandlersEventMap),
>(
  event: TKey,
  handler: (event: TEvent) => void,
  deps: ReadonlyArray<unknown>,
  eventTarget: EventTarget = document,
) {
  const handlerCallback = useCallback(handler, [...deps, handler]);

  useEffect(() => {
    eventTarget?.addEventListener(
      event,
      handlerCallback as EventListener,
      false,
    );
    return () => {
      eventTarget?.removeEventListener(
        event,
        handlerCallback as EventListener,
        false,
      );
    };
  }, [event, handlerCallback, eventTarget]);
}

export interface VisibilityController {
  setVisible: (value: ((prevState: boolean) => boolean) | boolean) => void;
  closeCallback: () => void;
  toggleCallback: () => void;
  isVisible: boolean;
}

/**
 *
 * @param defaultVisibility
 */
export function useVisibility(
  defaultVisibility: boolean,
): VisibilityController {
  const [isVisible, setVisible] = useState<boolean>(defaultVisibility);
  const closeCallback = useCallback(() => setVisible(false), []);
  const toggleCallback = useCallback(() => setVisible(!isVisible), [isVisible]);

  return {isVisible, setVisible, closeCallback, toggleCallback};
}

interface Coordinates {
  x: number;
  y: number;
}

export interface SwipeResult {
  direction: SwipeDirection | null;
  distance: number;
}

export enum SwipeDirection {
  Up = 'up',
  Down = 'down',
  Left = 'left',
  Right = 'right',
}

export const useSwipe = (
  swipeAreaRef: React.RefObject<HTMLElement>,
  swipeEndCallback: (swipe: SwipeResult) => void,
  swipeMoveCallback?: (swipe: SwipeResult) => void,
) => {
  const startRef = useRef<Coordinates | null>(null);

  const handleSwipe = (start: Coordinates, end: Coordinates) => {
    const result: SwipeResult = {
      direction: null,
      distance: 0,
    };

    if (start && end) {
      const diffX = start.x - end.x;
      const diffY = start.y - end.y;

      if (Math.abs(diffX) > Math.abs(diffY)) {
        result.direction =
          diffX > 0 ? SwipeDirection.Left : SwipeDirection.Right;
      } else {
        result.direction = diffY > 0 ? SwipeDirection.Up : SwipeDirection.Down;
      }

      result.distance = Math.sqrt(diffX ** 2 + diffY ** 2);
    }

    return result;
  };

  const touchStartHandler = (event: TouchEvent) => {
    startRef.current = {
      x: event.touches[0].clientX,
      y: event.touches[0].clientY,
    };
  };

  const touchEndHandler = useCallback(
    (event: TouchEvent) => {
      const end = {
        x: event.changedTouches[0].clientX,
        y: event.changedTouches[0].clientY,
      };

      const start = startRef.current;
      if (start) {
        const result: SwipeResult = handleSwipe(start, end);
        swipeEndCallback(result);
      }
    },
    [swipeEndCallback],
  );

  const touchMoveHandler = useCallback(
    (event: TouchEvent) => {
      if (!swipeMoveCallback) return;

      const end = {
        x: event.changedTouches[0].clientX,
        y: event.changedTouches[0].clientY,
      };

      const start = startRef.current;
      if (start) {
        const result: SwipeResult = handleSwipe(start, end);
        swipeMoveCallback(result);
      }
    },
    [swipeMoveCallback],
  );

  useEffect(() => {
    const element = swipeAreaRef.current;

    if (element !== null) {
      element.addEventListener('touchstart', touchStartHandler);
      element.addEventListener('touchmove', touchMoveHandler);
      element.addEventListener('touchend', touchEndHandler);
    }

    return () => {
      startRef.current = null;

      if (element !== null) {
        element.removeEventListener('touchstart', touchStartHandler);
        element.removeEventListener('touchmove', touchMoveHandler);
        element.removeEventListener('touchend', touchEndHandler);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swipeAreaRef.current, touchEndHandler, touchMoveHandler]);
};

interface AttachedObserverApi {
  observe(): void;
  unobserve(): void;
  disconnect(): void;
}

export function useResizeObserver(
  elementRef: RefObject<HTMLElement | null>,
  callback: (rect: DOMRectReadOnly) => void,
  debounceTime = 40,
) {
  const apiRef = useRef<AttachedObserverApi | null>(null);
  const element = elementRef.current;

  useEffect(() => {
    const resizeObserver = new ResizeObserver(
      debounce((entries: ResizeObserverEntry[]) => {
        entries.forEach((entry) => {
          callback(entry.contentRect);
        });
      }, debounceTime),
    );

    const api = {
      observe: () => element && resizeObserver.observe(element),
      unobserve: () => element && resizeObserver.unobserve(element),
      disconnect: () => resizeObserver.disconnect(),
    };

    apiRef.current?.disconnect();
    apiRef.current = api;

    api.observe();

    return () => {
      api.disconnect();
    };
  }, [callback, debounceTime, element]);

  return apiRef;
}

export function useShadowRootOnBody() {
  const shadowRootRef = useRef<ShadowRoot | null>(null);

  useLayoutEffect(() => {
    if (!shadowRootRef.current) {
      const div = document.createElement('div');
      div.style.display = 'block';
      document.body.appendChild(div);
      shadowRootRef.current = div.attachShadow({mode: 'open'});
      shadowRootRef.current.append(...getStyleTags());
    }

    return () => {
      if (shadowRootRef.current) {
        shadowRootRef.current.host?.remove();
      }
    };
  }, []);

  return shadowRootRef;
}

export function useStoredState<TKey extends CacheKey>(
  key: TKey,
  initialValue?: CacheValueMapping[TKey],
) {
  const storage = useRef<TypedStorage>(getStorage());
  const [value, setValue] = useState<CacheValueMapping[TKey]>(
    storage.current?.getItem(key) ?? initialValue ?? null,
  );

  useEffect(() => storage.current?.setItem(key, value), [key, value]);

  return [value, setValue] as const;
}
